/**
 * Widget Order Application Service
 *
 * Orchestrates order operations for widget customers.
 * Handles authorization checks, data transformation, and API calls.
 *
 * Following DDD principles:
 * - NO business logic (backend owns that)
 * - Transforms DTOs → View Models
 * - Enforces frontend authorization checks
 * - Provides stable interface for UI components
 */

import type { WidgetOrderService } from '../../services/widget/WidgetOrderService';
import type { Order, OrderStatus, OrderFilters } from '../../types/widget/order';
import type {
  OrderDetailViewModel,
  OrderListItemViewModel,
  OrderAuthorizationViewModel,
  StatusBadgeViewModel,
  OrderDatesViewModel,
  OrderPricingViewModel,
} from './view-models/OrderViewModel';

export class WidgetOrderApplicationService {
  constructor(
    private orderService: WidgetOrderService,
    // @ts-expect-error - Reserved for future authorization checks
    private _customerId: string
  ) {}

  /**
   * Get single order with authorization check
   */
  async getOrder(orderId: string): Promise<OrderDetailViewModel> {
    // Fetch from backend
    const order = await this.orderService.getOrder(orderId);

    // Check authorization (basic check - backend is authoritative)
    const auth = this.checkAuthorization(order);
    if (!auth.canView) {
      throw new Error('You are not authorized to view this order');
    }

    // Transform to view model
    return this.toDetailViewModel(order);
  }

  /**
   * Get list of orders with pagination
   */
  async getOrders(filters?: OrderFilters): Promise<{
    orders: OrderListItemViewModel[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const response = await this.orderService.listOrders(filters);

    return {
      orders: response.orders.map((order) => this.toListItemViewModel(order)),
      total: response.total,
      page: response.page,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
    };
  }

  /**
   * Request pickup for an order
   */
  async requestPickup(
    orderId: string,
    data: { requestedPickupDate?: string; notes?: string }
  ): Promise<OrderDetailViewModel> {
    // Backend will perform authoritative authorization check
    const updatedOrder = await this.orderService.requestPickup(orderId, data);

    return this.toDetailViewModel(updatedOrder);
  }

  /**
   * Cancel an order
   */
  async cancelOrder(
    orderId: string,
    data: { reason: string; notes?: string }
  ): Promise<void> {
    // Backend will perform authoritative authorization check
    await this.orderService.cancelOrder(orderId, data);
  }

  /**
   * Check if customer can perform actions on this order
   * NOTE: This is a frontend UX optimization. Backend is authoritative.
   */
  private checkAuthorization(order: Order): OrderAuthorizationViewModel {
    // For now, assume customer owns all orders returned by API
    // Backend should filter orders by customer
    const canView = true;

    // Business rules for actions (mirrored from backend)
    const canRequestPickup =
      order.status === 'delivered' || order.status === 'in_use';

    const canCancel = order.status === 'pending' || order.status === 'scheduled';

    return {
      canView,
      canRequestPickup,
      canCancel,
    };
  }

  /**
   * Transform Order DTO to detail view model
   */
  private toDetailViewModel(order: Order): OrderDetailViewModel {
    const auth = this.checkAuthorization(order);

    return {
      id: order.id,
      orderNumber: order.orderNumber,
      status: order.status,
      statusBadge: this.getStatusBadge(order.status),
      containerSize: order.containerSize,
      materialType: order.materialType,
      orderType: this.formatOrderType(order.orderType),
      serviceAddress: {
        street: order.serviceAddress.street,
        cityStateZip: `${order.serviceAddress.city}, ${order.serviceAddress.state} ${order.serviceAddress.zipCode}`,
      },
      placementInstructions: order.placementInstructions,
      contactName: order.contactName,
      contactEmail: order.contactEmail,
      contactPhone: order.contactPhone,
      dates: this.formatDates(order),
      pricing: order.pricing ? this.formatPricing(order.pricing) : undefined,
      providerName: order.providerName,
      providerPhone: order.providerPhone,
      providerEmail: order.providerEmail,
      actions: {
        canRequestPickup: auth.canRequestPickup,
        canCancel: auth.canCancel,
        canViewPricing: !!order.pricing,
      },
      rawData: order,
    };
  }

  /**
   * Transform Order DTO to list item view model
   */
  private toListItemViewModel(order: Order): OrderListItemViewModel {
    return {
      id: order.id,
      orderNumber: order.orderNumber,
      status: order.status,
      statusBadge: this.getStatusBadge(order.status),
      containerSize: order.containerSize,
      materialType: order.materialType,
      deliveryDate: this.formatShortDate(order.requestedDeliveryDate),
      cityState: `${order.serviceAddress.city}, ${order.serviceAddress.state}`,
      canViewDetail: true,
    };
  }

  /**
   * Get status badge configuration for UI
   */
  private getStatusBadge(status: OrderStatus): StatusBadgeViewModel {
    const badges: Record<OrderStatus, StatusBadgeViewModel> = {
      pending: {
        label: 'Pending',
        color: 'gray',
        icon: 'clock',
      },
      scheduled: {
        label: 'Scheduled',
        color: 'blue',
        icon: 'calendar',
      },
      delivered: {
        label: 'Delivered',
        color: 'green',
        icon: 'check',
      },
      in_use: {
        label: 'In Use',
        color: 'green',
        icon: 'box',
      },
      pickup_requested: {
        label: 'Pickup Requested',
        color: 'yellow',
        icon: 'truck',
      },
      picked_up: {
        label: 'Picked Up',
        color: 'blue',
        icon: 'package',
      },
      completed: {
        label: 'Completed',
        color: 'green',
        icon: 'check-circle',
      },
      cancelled: {
        label: 'Cancelled',
        color: 'red',
        icon: 'x-circle',
      },
    };

    return badges[status];
  }

  /**
   * Format order type for display
   */
  private formatOrderType(orderType: string): string {
    return orderType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Format all dates for order detail display
   */
  private formatDates(order: Order): OrderDatesViewModel {
    return {
      placedOn: this.formatLongDate(order.createdAt),
      requestedDelivery: this.formatLongDate(order.requestedDeliveryDate),
      scheduledDelivery: order.scheduledDeliveryDate
        ? this.formatDateTime(order.scheduledDeliveryDate)
        : undefined,
      actualDelivery: order.actualDeliveryDate
        ? this.formatDateTime(order.actualDeliveryDate)
        : undefined,
      requestedPickup: order.requestedPickupDate
        ? this.formatLongDate(order.requestedPickupDate)
        : undefined,
      scheduledPickup: order.scheduledPickupDate
        ? this.formatDateTime(order.scheduledPickupDate)
        : undefined,
      actualPickup: order.actualPickupDate
        ? this.formatDateTime(order.actualPickupDate)
        : undefined,
    };
  }

  /**
   * Format pricing for display
   */
  private formatPricing(pricing: {
    rental: number;
    delivery: number;
    disposal: number;
    total: number;
  }): OrderPricingViewModel {
    return {
      rental: this.formatCurrency(pricing.rental),
      delivery: this.formatCurrency(pricing.delivery),
      disposal: this.formatCurrency(pricing.disposal),
      total: this.formatCurrency(pricing.total),
    };
  }

  /**
   * Format long date: "Monday, January 22, 2025"
   */
  private formatLongDate(isoDate: string): string {
    try {
      return new Date(isoDate).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  }

  /**
   * Format short date: "Jan 22, 2025"
   */
  private formatShortDate(isoDate: string): string {
    try {
      return new Date(isoDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  }

  /**
   * Format date with time: "Mon, Jan 22, 2025, 2:00 PM"
   */
  private formatDateTime(isoDate: string): string {
    try {
      return new Date(isoDate).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  }

  /**
   * Format currency: "$150.00"
   */
  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }
}
